import {
  AddressInput,
  PlainInput,
  Radio,
  RadioGroup,
  Checkbox,
} from "@worthy-npm/clarity";
import {
  Button,
  Divider,
  WorthyThemeProvider,
} from "@worthy-npm/worthy-ui-components";
import I18n from "lib/i18n/i18n";
import React from "react";
import { isMobile } from "../../helpers/ApplicationHelper";
import { getShipBackText } from "../item/shipBack/helpers";
import Popup from "./popup";

const SAME_ADDRESS = "same_address";
const NEW_ADDRESS = "new_address";

class ShipBackAddressUpdatePopup extends Popup {
  constructor(props) {
    super(props);

    this.state = {
      isNewAddress: false,
      isPrivateResidence: false,
      address: {
        street: "",
        street2: "",
        city: "",
        state: "",
        zipcode: "",
      },
      hasValidAddress: false,
      isSaving: false,
    };
    this.intercom = null;
  }

  static initPopup(props = {}) {
    props.stopAnimation ? props.stopAnimation() : null;
    Popup.initPopup.call(this, props);
  }

  componentDidMount() {
    if (isMobile()) {
      this.intercom =
        document.querySelector(".intercom-launcher") ||
        document.querySelector(".intercom-app");
    }
    if (this.intercom) {
      this.intercom.style.display = "none";
    }
  }

  componentWillUnmount() {
    if (this.intercom) {
      this.intercom.style.display = "initial";
    }
  }

  _onPopupClose() {
    if (this.props.stopAnimation) {
      this.props.stopAnimation();
    }
  }

  _hide = () => {
    super._hide();
  };

  onAddressSelected = (address) => {
    const compiledAddress = address.address_components.reduce(
      (all, component) => {
        return {
          ...all,
          [component.types[0]]: component.short_name,
        };
      },
      {}
    );
    const {
      street_number,
      route,
      locality,
      sublocality_level_1,
      administrative_area_level_3,
      administrative_area_level_1,
      postal_code,
    } = compiledAddress;

    const streetNumber = street_number ? street_number + " " : "";
    const street = streetNumber + (route ? route : "");
    const city = locality || sublocality_level_1 || administrative_area_level_3;
    const state = administrative_area_level_1;
    const zipcode = postal_code;

    this.setState((prevState) => ({
      address: {
        ...prevState.address,
        street,
        city,
        state,
        zipcode,
      },
      hasValidAddress: true,
      isSaving: false,
    }));
  };

  onAddressTyped = () => {
    this.setState({ hasValidAddress: false });
  };

  setAptNumber = (val) => {
    this.setState((prevState) => ({
      address: {
        ...prevState.address,
        street2: val,
      },
      isPrivateResidence: false,
    }));
  };

  onPrivateResidenceToggle = () => {
    this.setState((prevState) => ({
      address: {
        ...prevState.address,
        street2: "",
      },
      isPrivateResidence: !prevState.isPrivateResidence,
    }));
  };

  toggleSameOrNewAddress = (val) => {
    this.setState({ isNewAddress: val === NEW_ADDRESS });
  };

  onAddressSave = async () => {
    const { onSave } = this.props;
    const { address, isNewAddress, hasValidAddress } = this.state;
    if (isNewAddress && hasValidAddress) {
      this.setState({ isSaving: true });
      await onSave(address);
      this.setState({ isSaving: false });
    }
    this._hide();
  };

  title = () => {
    return (
      <>
        {getShipBackText("updateAddressModalHeader")}
        <br />
        <div className="shipBack--text" style={{ marginTop: 10 }}>
          {getShipBackText("updateAddressModalSubHeader")}
        </div>
      </>
    );
  };

  newAddress = () => {
    const { showAddressLine2 } = this.props;
    const { isNewAddress, isPrivateResidence, isSaving } = this.state;
    return (
      <div style={{ marginLeft: 40 }}>
        <AddressInput
          addressSelected={this.onAddressSelected}
          label={I18n.getString("listingPage::labelAddress")}
          errorMessage={I18n.getString("listingPage::addressErrorMessage")}
          typingMessage={I18n.getString("listingPage::typingInMessage")}
          placeholder={I18n.getString(
            "itemPage::AddressWaiting::consts::placeholder"
          )}
          description={I18n.getString(
            "itemPage::AddressWaiting::consts::onFocusMsgShort"
          )}
          showResultsDown={isMobile()}
          change={this.onAddressTyped}
          disabled={!isNewAddress || isSaving}
        />
        {showAddressLine2 && (
          <>
            <PlainInput
              name="apt-number-input"
              label={I18n.getString("listingPage::labelApt")}
              disabled={isPrivateResidence || !isNewAddress || isSaving}
              value={this.state.address.street2}
              change={this.setAptNumber}
              blur={this.setAptNumber}
            />
            <div className={"private-residence checkbox-field-wrapper"}>
              <Checkbox
                key={"apt-is-private-checkbox"}
                name="apt-is-private"
                label={I18n.getString("listingPage::labelPrivateResidence")}
                checked={isPrivateResidence}
                onChange={this.onPrivateResidenceToggle}
                disabled={!isNewAddress || isSaving}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  footer = () => {
    const { showAddressLine2 } = this.props;
    const {
      isNewAddress,
      hasValidAddress,
      isSaving,
      isPrivateResidence,
      address,
    } = this.state;
    const isInvalidAddress = isNewAddress && !hasValidAddress;
    const isIncompleteAddress =
      isNewAddress &&
      showAddressLine2 &&
      !isPrivateResidence &&
      address.street2.length === 0;

    return (
      <>
        <div className="shipBack--button-group">
          <Button variant="text" onClick={this._hide}>
            {getShipBackText("cancel")}
          </Button>
          <Button
            onClick={this.onAddressSave}
            isDisabled={isSaving || isInvalidAddress || isIncompleteAddress}
          >
            {getShipBackText("save")}
          </Button>
        </div>
      </>
    );
  };

  content = () => {
    const { processingRequest } = this.state;
    const { formattedAddress } = this.props;
    return (
      <WorthyThemeProvider>
        <RadioGroup
          name={"shipment-method"}
          initialValue={SAME_ADDRESS}
          disabled={processingRequest}
          onChange={(value) => this.toggleSameOrNewAddress(value)}
        >
          <div className="continue-with-address shipBack-address-wrapper">
            <Radio
              value={SAME_ADDRESS}
              label={`<span>Continue with <b>${formattedAddress}</b></span>`}
              disabled={false}
            />
          </div>
          <div className="new-address shipBack-address-wrapper">
            <Radio
              value={NEW_ADDRESS}
              label={I18n.getString("listingPage::labelNewAddress")}
              disabled={false}
            />
            {this.newAddress()}
          </div>
        </RadioGroup>
        <Divider borderColor="#F4F4F4" style={{ marginTop: 20 }} />

        {this.footer()}
      </WorthyThemeProvider>
    );
  };
}

ShipBackAddressUpdatePopup.defaultProps = {
  popupClassName: "shipBack shipBack--modal",
  single: true,
  modal: true,
  showAddressLine2: true,
  onPopupClose: function () {
    this._onPopupClose();
  },
};

export default ShipBackAddressUpdatePopup;
